import React, { useState, useCallback } from 'react';
import { Box, SortDirection, Typography } from '@material-ui/core';
import { format } from 'date-fns';

import { PaginatedWrappedComponentProps, withPagination } from 'features/app/hocs/withPagination';
import { Table } from 'features/app/components/Table';
import { Search } from 'features/app/components/Search';
import { RangeDatePicker } from 'shared/date-pickers/RangeDatePicker';
import { CardStatus } from 'features/redemptions/components/CardStatus';
import BaseButton from 'features/app/components/BaseButton';
import { Tabs } from 'shared/tabs';

import { TableColDef, TableCellDef, TableRowActions } from 'features/app/ts/Table';
import { CardStatusType, RedemptionReport } from 'features/redemptions/ts/RedemptionReport';
import { RedemptionReportFilters } from 'features/redemptions/ts/RedemptionReportFilters';

import { getRedemptionReportCsvRequest, cancelCardRequest } from 'features/redemptions/api';
import { orderByToOrdering, orderingToSortDirection } from 'features/redemptions/utils/mapOrdering';
import { STATUS_DICTIONARY } from 'features/redemptions/constants/statusDictionary';

import { useTranslation } from 'hooks/useTranslation';
import { useMessage } from 'features/message/hooks/useMessage';
import { t } from 'locales/i18n';

import { FileUtils } from 'utils/FileUtils';
import { ErrorUtils } from 'utils';

import { useStyles } from './styles';
import { queryClient } from 'api/query';
import CancelCardModal from 'features/redemptions/components/Redemptions/CancelCardModal';

const columns: TableColDef[] = [
  { field: 'order_id', label: t('columns.orderId', { ns: 'redemptions' }) },
  { field: 'gift_card', label: t('columns.cardId', { ns: 'redemptions' }) },
  { field: 'date', label: t('columns.date', { ns: 'redemptions' }), sortable: true },
  { field: 'recipient_name', label: t('columns.recipientName', { ns: 'redemptions' }) },
  { field: 'product', label: t('columns.product', { ns: 'redemptions' }) },
  { field: 'denomination', label: t('columns.denomination', { ns: 'redemptions' }) },
  { field: 'currency_code', label: t('columns.currency', { ns: 'redemptions' }) },
  { field: 'card_status', label: t('columns.cardStatus', { ns: 'redemptions' }) },
];

const cells: TableCellDef[] = [
  {
    field: 'order_id',
  },
  {
    field: 'gift_card',
  },
  {
    field: 'date',
    component: ({ item }) => {
      const classes = useStyles();

      return <Typography className={classes.defaultText}>{format(new Date(item as string), 'dd/MM/yyyy')}</Typography>;
    },
  },
  {
    field: 'recipient_name',
  },
  {
    field: 'product',
  },
  {
    field: 'denomination',
    component: ({ item }) => {
      const classes = useStyles();

      return <Typography className={classes.strongText}>{Number(item).toFixed(2)}</Typography>;
    },
  },
  {
    field: 'currency_code',
  },
  {
    field: 'card_status',
    component: ({ item }) => <CardStatus colorScheme={STATUS_DICTIONARY[item as RedemptionReport['card_status']]} />,
  },
];

const statusFilterTabs = [
  { value: '', label: t('filters.tabs.all', { ns: 'redemptions' }) },
  { value: CardStatusType.ACTIVE, label: t('filters.tabs.active', { ns: 'redemptions' }) },
  { value: CardStatusType.REDEEMED, label: t('filters.tabs.redeemed', { ns: 'redemptions' }) },
  { value: CardStatusType.CANCELED, label: t('filters.tabs.canceled', { ns: 'redemptions' }) },
  { value: CardStatusType.EXPIRED, label: t('filters.tabs.expired', { ns: 'redemptions' }) },
];

interface RedemptionProps {}

const redemptions = (props: RedemptionProps) => {
  const { filters, setFilters, data, isLoading, page, pageSize, refetch } = props as PaginatedWrappedComponentProps<
    RedemptionReport,
    RedemptionReportFilters
  >;

  const { t } = useTranslation('redemptions');
  const classes = useStyles();

  const [isDownloading, setIsDownloading] = useState(false);
  const { showErrorMessage, showSuccessMessage } = useMessage();

  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState<RedemptionReport['gift_card'] | null>(null);

  const handleClose = () => {
    setOpen(false);
  };

  const handleCardStatusChange = (_: unknown, value: RedemptionReport['card_status']) => {
    setFilters({ ...filters, card_status: value });
  };

  const createFilterChangeHandler = (filter: keyof RedemptionReportFilters) => {
    return (value: unknown) => {
      setFilters({ ...filters, [filter]: value });
    };
  };

  const handleSortChange = useCallback(
    (_: string, orderBy: SortDirection) => {
      setFilters({ ...filters, ordering: orderByToOrdering(orderBy) });
    },
    [filters, setFilters]
  );

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    showSuccessMessage(t('downloadStartedMessage'));

    try {
      const { data } = await getRedemptionReportCsvRequest({ ...filters, page, page_size: pageSize });

      FileUtils.downloadFile(data);
      showSuccessMessage(t('reportDownloadedMessage'));
    } catch (error) {
      showErrorMessage(ErrorUtils.convertError(error));
    }

    setIsDownloading(false);
  };

  const handleCancelCard = async (item: RedemptionReport['gift_card'] | null) => {
    if (!item) return;

    setOpen(false);

    try {
      await cancelCardRequest(item);
      await queryClient.invalidateQueries('partner-balance');
      showSuccessMessage(t('cancelCardMessage'));
      refetch();
    } catch (error) {
      showErrorMessage(ErrorUtils.convertError(error));
    }
  };

  const rowActions: TableRowActions[] = [
    {
      label: t('actions.cancelCard', { ns: 'redemptions' }),
      event: item => {
        setOpen(true);
        setCardId((item as RedemptionReport).gift_card);
      },
      shouldHide: (item: RedemptionReport) => item?.card_status !== CardStatusType.ACTIVE,
    },
  ];

  return (
    <Box className={classes.root}>
      <Box className={classes.filtersContainer}>
        <Box className={classes.filters}>
          <Box>
            <Tabs onChange={handleCardStatusChange} value={filters.card_status ?? ''} tabs={statusFilterTabs} />
          </Box>
          <Box className={classes.search}>
            <Search value={filters.search} onChange={createFilterChangeHandler('search')} />
          </Box>
        </Box>
        <Box className={classes.datePickerContainer}>
          <RangeDatePicker
            startDate={filters.created_at_after}
            endDate={filters.created_at_before}
            onEndDateChange={createFilterChangeHandler('created_at_before')}
            onStartDateChange={createFilterChangeHandler('created_at_after')}
          />
          <BaseButton className={classes.downloadButton} onClick={handleDownloadClick} isLoading={isDownloading}>
            {t('filters.downloadButton')}
          </BaseButton>
        </Box>
      </Box>

      <Table
        columns={columns}
        rowsActions={rowActions}
        cells={cells}
        data={data ?? []}
        initialLoading={isLoading}
        ariaLabel={t('ariaLabel')}
        empty={t('empty')}
        onSort={handleSortChange}
        orderBy="date"
        sortDirection={orderingToSortDirection(filters.ordering)}
      />

      <CancelCardModal onSubmit={() => handleCancelCard(cardId)} onClose={handleClose} open={open} cardId={cardId} />
    </Box>
  );
};

export const Redemptions = withPagination(
  {
    getEndpoint: '/orders/redemptions/',
    queryId: 'redemptions',
    pageSize: 8,
    defaultFilters: {
      ordering: '-created_at',
    },
  },
  redemptions
);
